/**
 * ProjectInvoicesList Component
 * Displays invoices for a specific project with navigation to invoice detail/creation
 *
 * Phase 7.5.19: Added to ProjectDetailPage to show project invoices
 *
 * Features:
 * - Lists all invoices for the project
 * - Shows invoice status, amount, and date
 * - Links to invoice detail page
 * - "Create Invoice" button navigates to invoice creation with project pre-selected
 *
 * @see docs/patterns/frontend/REACT_QUERY_PATTERNS.md
 */
import { useNavigate } from 'react-router-dom';
import { useInvoices } from '@/hooks';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, FileText } from 'lucide-react';
import { formatDateOnly } from '@/lib/utils';

interface ProjectInvoicesListProps {
  projectId: string;
  hideHeader?: boolean; // 9.5c: Hide when wrapped in CollapsibleSection
}

// Invoice status badge colors
function getStatusBadgeVariant(status: string, displayStatus?: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  const effectiveStatus = displayStatus || status;
  switch (effectiveStatus) {
    case 'paid':
      return 'default'; // Green-ish
    case 'sent':
      return 'secondary'; // Gray
    case 'overdue':
      return 'destructive'; // Red
    case 'draft':
    default:
      return 'outline'; // Bordered
  }
}

function formatCurrency(amount: number | string): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  // invoice_date is a DATE column (no time component). Parsing it with the
  // bare `new Date(dateString)` constructor treats it as UTC midnight, which
  // renders one calendar day early for anyone west of UTC (e.g. America/Edmonton).
  // formatDateOnly (src/lib/utils.ts) parses the y/m/d components directly so
  // the displayed date always matches the stored calendar date.
  return formatDateOnly(dateString, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function ProjectInvoicesList({ projectId, hideHeader = false }: ProjectInvoicesListProps) {
  const navigate = useNavigate();

  // Fetch invoices for this project
  const { data: invoicesData, isLoading } = useInvoices({ project_id: projectId });

  const invoices = invoicesData?.invoices || [];

  const handleCreateInvoice = () => {
    // Navigate to invoice creation with project pre-selected
    navigate('/invoices/new', { state: { projectId } });
  };

  const handleViewInvoice = (invoiceId: string) => {
    navigate(`/invoices/${invoiceId}`);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Invoices</h3>
        </div>
        <div className="space-y-2">
          {[...Array(2)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="project-invoices-list">
      {/* 9.5c: hideHeader when wrapped in CollapsibleSection */}
      {!hideHeader ? (
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Invoices ({invoices.length})</h3>
          <Button onClick={handleCreateInvoice}>
            <Plus className="h-4 w-4 mr-2" />
            Create Invoice
          </Button>
        </div>
      ) : (
        <div className="flex justify-end">
          <Button onClick={handleCreateInvoice}>
            <Plus className="h-4 w-4 mr-2" />
            Create Invoice
          </Button>
        </div>
      )}

      {invoices.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed rounded-lg">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-2">No invoices yet</p>
          <p className="text-sm text-muted-foreground mb-4">
            Create an invoice to bill for confirmed work on this project.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {invoices.map((invoice) => (
            <div
              key={invoice.id}
              className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
              onClick={() => handleViewInvoice(invoice.id)}
              data-testid={`invoice-row-${invoice.id}`}
            >
              <div className="flex items-center gap-4">
                <div>
                  <p className="font-medium">#{invoice.invoice_number}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(invoice.invoice_date)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="font-medium">{formatCurrency(invoice.total_amount)}</p>
                  {invoice.amount_paid > 0 && (
                    <p className="text-sm text-emerald-600">
                      Paid: {formatCurrency(invoice.amount_paid)}
                      {(invoice.amount_paid ?? 0) > (invoice.total_amount ?? 0) && (
                        <span className="ml-1 text-xs text-amber-700">
                          (excess {formatCurrency((invoice.amount_paid ?? 0) - (invoice.total_amount ?? 0))} held)
                        </span>
                      )}
                    </p>
                  )}
                </div>
                <Badge variant={getStatusBadgeVariant(invoice.status, invoice.display_status)}>
                  {(invoice.display_status || invoice.status).charAt(0).toUpperCase() +
                    (invoice.display_status || invoice.status).slice(1)}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
